import { Card, Divider, Flex, Space, Tabs, Typography } from 'antd'
import { createStyles } from 'antd-style'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { PublicAccessCredentialRead } from 'src/api/main'

import {
  AppPermissionPanel,
  CredentialMetadataDescriptions
} from '../../components/credentials'

const { Text } = Typography

const useStyles = createStyles(({ token }) => ({
  tabCount: {
    color: token.colorTextTertiary,
    fontSize: 11,
    fontVariantNumeric: 'tabular-nums'
  }
}))

type Props = {
  credential: PublicAccessCredentialRead
}

export const OverviewCard = ({ credential }: Props) => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const apps = credential.apps ?? []

  // The active tab tracks which app's permissions are shown. If `apps`
  // changes underneath us (rotate, update) and the previous key disappears,
  // the `activeBinding` fallback resolves to the first app so the panel
  // never renders empty.
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined)
  const activeBinding =
    apps.find((a) => a.saas_app_token === activeKey) ?? apps[0]

  return (
    <Card title={t('workspace.publicAccess.detail.overview.title')}>
      <CredentialMetadataDescriptions credential={credential} />

      <Divider style={{ marginBlock: 20 }} />

      <Flex vertical gap={12}>
        <Flex align="baseline" gap={8}>
          <Text strong style={{ fontSize: 13 }}>
            {t('workspace.publicAccess.bindings.label')}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('workspace.publicAccess.columns.appsCount', {
              count: apps.length
            })}
          </Text>
        </Flex>

        {apps.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t('workspace.publicAccess.bindings.none')}
          </Text>
        ) : (
          <Flex vertical gap={12}>
            <Tabs
              size="small"
              activeKey={activeBinding?.saas_app_token}
              onChange={setActiveKey}
              tabBarStyle={{ marginBottom: 0 }}
              items={apps.map((binding) => ({
                key: binding.saas_app_token,
                label: (
                  <Space size={6}>
                    <span>{binding.app_code}</span>
                    <span className={styles.tabCount}>
                      {binding.permission_codes.length}
                    </span>
                  </Space>
                )
              }))}
            />

            {activeBinding ? (
              <AppPermissionPanel
                permissionCodes={activeBinding.permission_codes}
              />
            ) : null}
          </Flex>
        )}
      </Flex>
    </Card>
  )
}
